import React from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import {
    Box,
    Container,
    Heading,
    Text,
    Button,
    Link,
    VStack,
    Image,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet";

export default function About() {
    return (
        <Box bg="offwhite.100" minH="100vh">
            <Helmet>
                <title>About Digitalfields Europe Ltd | BetaPersona</title>
                <meta name="description" content="A UK company. Occupational psychology, not clinical. BetaPersona is a product of Digitalfields Europe Ltd. We build assessment-report systems for employers, assessment practices and executive coaches." />
            </Helmet>

            <Container maxW="container.lg" py={{ base: 12, md: 20 }}>
                <VStack spacing={10} align="stretch">
                    <Box>
                        <Heading as="h1" size="2xl" mb={6}>
                            A UK company. Occupational, not clinical.
                        </Heading>
                        <Image
                            src="/images/about-9301243.jpeg"
                            alt="Professional at work. Photo: Mikhail Nilov, Pexels"
                            borderRadius="md"
                            w="full"
                            h="auto"
                            objectFit="cover"
                            aspectRatio="16/9"
                            mb={8}
                        />
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={4}>
                            Company
                        </Heading>
                        <VStack align="start" spacing={3}>
                            <Text>
                                <strong>Digitalfields Europe Ltd</strong>
                            </Text>
                            <Text>Company number: 15999199</Text>
                            <Text>
                                Registered office: 85 Great Portland Street, First Floor, London W1W 7LT, United Kingdom
                            </Text>
                        </VStack>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={4}>
                            Product
                        </Heading>
                        <Text mb={4}>
                            BetaPersona is a repository, integration platform and report-generation system for occupational assessment. We work with employers (Head of Talent, HRBP, in-house occupational psychologists), assessment practices, and executive coaches who still run a psychometric battery.
                        </Text>
                        <Text>
                            The platform overlays beside publisher systems (GeneSys, Hogan, SHL, Thomas). We do not replace the test library. We connect, store, apply access policy, and generate business-ready packs derived from publisher scores and the client's competency framework.
                        </Text>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={4}>
                            Experience
                        </Heading>
                        <Text>
                            Years building assessment-report systems for international clients.
                        </Text>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={4}>
                            The name
                        </Heading>
                        <Text>
                            Betapersona: implies - Constantly assessing the person to promote the best fit and path.
                        </Text>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={4}>
                            Standards
                        </Heading>
                        <Text mb={4}>
                            Occupational assessment under British Psychological Society and European Federation of Psychologists' Associations standards. We align to those registers for qualified test use.
                        </Text>
                        <Text>
                            We build systems. We do not interpret assessments. The occupational psychologist, the qualified HRBP, or the assessment practice are the qualified users. BetaPersona stores, applies access policy, and generates the derived report.
                        </Text>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={4}>
                            Scope
                        </Heading>
                        <Text mb={4}>
                            <strong>Occupational</strong>: recruitment, promotion, succession planning, leadership development. Employer buys after shortlist. Assessment practices. Executive coaches who still run a battery.
                        </Text>
                        <Text>
                            <strong>Not clinical</strong>: we do not process clinical-psychology data, therapeutic assessments, or health records.
                        </Text>
                    </Box>

                    <Box bg="navy.500" color="white" p={8} borderRadius="md">
                        <Heading as="h3" size="lg" mb={4}>
                            Want to know more?
                        </Heading>
                        <Text mb={6}>
                            We will walk you through the platform, answer questions about hosting, qualified test use, and pricing.
                        </Text>
                        <Link
                            as={ReactRouterLink}
                            to="/contact"
                            _hover={{ textDecoration: "none" }}
                        >
                            <Button
                                bg="accent.orange"
                                color="white"
                                size="lg"
                                _hover={{ bg: "rgba(248, 100, 30, 0.85)" }}
                            >
                                Talk to us
                            </Button>
                        </Link>
                        <Text mt={4} fontSize="sm">
                            A working session with talent, HRBP or in-house occupational psychology. Assessment practices and executive coaches who still run a battery: same door.
                        </Text>
                    </Box>
                </VStack>
            </Container>
        </Box>
    );
}
